import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, UploadCloud, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Input } from '@/components/ui';
import { Label } from '@/components/ui/label';
import type { AssetType, MerchantAsset } from '@/types/merchant-asset';

interface PropagationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: MerchantAsset | null;
  onConfirm: (newFileUrl: string) => void;
  isPending?: boolean;
  /**
   * Upload a replacement file via the BFF pre-signed proxy → returns the resulting
   * CDN URL (Issue #379). Same signature as the create dialog; the dialog calls it
   * with the asset's fixed `type` as `category`. When absent, no upload control is
   * shown and the dialog stays URL-only (backward-compatible).
   */
  onUpload?: (file: File, type: AssetType) => Promise<string>;
}

const HTTPS_URL = /^https:\/\/.+/;

/**
 * "Replace file" dialog (Issue #417): collects the new file URL for a replacement.
 * The asset is NOT swapped in place — the Facade has no in-place swap (a
 * currentFileUrl PUT would retire the asset to status=replaced). Confirming creates
 * a NEW asset (new id) and repoints every referencing service onto it
 * (orchestrated in useReplaceAsset). The dialog shows the affected services
 * (derived `usedBy`) so the operator sees the blast radius; when usage is
 * unavailable the warning is phrased conservatively (manual relink).
 *
 * The new URL can be filled either by uploading a file (pre-signed S3 PUT via the
 * BFF proxy, Issue #379) or by entering an existing HTTPS CDN URL — both feed the
 * same field. Last-write-wins: a successful upload fills the field, the operator can
 * still overwrite it by typing; a failed upload leaves the field untouched.
 */
export function PropagationDialog({ open, onOpenChange, asset, onConfirm, isPending, onUpload }: PropagationDialogProps) {
  const { t } = useTranslation('merchant-assets');
  const [newUrl, setNewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setNewUrl('');
      setUploading(false);
      setUploadError(false);
    }
  }, [open]);

  if (!asset) return null;

  const urlValid = HTTPS_URL.test(newUrl.trim()) && newUrl.trim().length <= 2048;
  const usedBy = asset.usedBy;

  const handleFile = async (file: File | undefined) => {
    if (!file || !onUpload) return;
    setUploadError(false);
    setUploading(true);
    try {
      // The asset's type is fixed — a stylesheet is replaced by a stylesheet, etc.
      // It travels as `category` through onUpload → uploadAssetFile.
      const url = await onUpload(file, asset.type);
      setNewUrl(url);
    } catch {
      setUploadError(true);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlValid || uploading) return;
    onConfirm(newUrl.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{t('propagate.title')}</DialogTitle>
            <DialogDescription>{t('propagate.intro')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {onUpload && (
              <div className="space-y-1.5">
                <input
                  ref={fileInputRef}
                  id="propagate-file"
                  type="file"
                  className="hidden"
                  onChange={(e) => void handleFile(e.target.files?.[0])}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('upsert.uploading')}</>
                  ) : (
                    <><UploadCloud className="mr-2 h-4 w-4" /> {t('upsert.uploadLabel')}</>
                  )}
                </Button>
                {uploadError && <p className="text-xs text-destructive">{t('upsert.uploadError')}</p>}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="propagate-url">{t('propagate.newUrlLabel')}</Label>
              <Input
                id="propagate-url"
                type="url"
                value={newUrl}
                maxLength={2048}
                placeholder={asset.currentFileUrl}
                onChange={(e) => setNewUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t('propagate.identityNote')}</p>
            </div>

            <div className="rounded-md border bg-muted/30 p-3">
              <p className="mb-1 text-xs font-medium text-muted-foreground">{t('propagate.affected')}</p>
              {asset.usageUnavailable ? (
                <p className="flex items-center gap-1 text-xs text-amber-600">
                  <AlertTriangle className="h-3 w-3 shrink-0" />
                  {t('propagate.affectedUnknown')}
                </p>
              ) : usedBy.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('propagate.affectedNone')}</p>
              ) : (
                <ul className="space-y-0.5 text-sm">
                  {usedBy.map((u) => (
                    <li key={u.serviceId}>{u.displayName}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={!urlValid || isPending || uploading}>
              {t('propagate.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
